import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { CompanySettingsApiService } from '../pages/company-settings/company-settings-api.service';
import { MyTenantBrandingApiData } from '../pages/company-settings/company-settings.models';
import { CrmBrandingStateService } from './crm-branding-state.service';

/**
 * Fallback for a change made by another user on another device. The real signal is
 * the hub push (see {@link CrmSignalRService.brandingUpdates$}); until the backend
 * emits it, a visible tab re-reads branding this often, and a tab coming back to the
 * foreground re-reads if its copy is older than this. Set to 0 to rely on the push alone.
 */
const REVALIDATE_AFTER_MS = 60_000;

/**
 * A user doing something (changing page, saving a form) re-reads branding straight
 * away rather than waiting for the timer, but at most once per this window so a
 * burst of clicks or saves is still a single request.
 */
const ACTIVITY_THROTTLE_MS = 5_000;

/** Same-browser channel: one tab saving branding tells its siblings to re-read immediately. */
const BRANDING_CHANNEL = 'crm-branding-sync';

/** localStorage key used as the fallback signal where BroadcastChannel is unavailable. */
const BRANDING_PING_KEY = 'crm-branding-updated-at';

interface BrandingSnapshot {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
}

/**
 * Single source of tenant branding for the CRM.
 *
 * `/business/my-tenant/branding` is read once when the shell starts and the
 * response is cached here for the rest of the session. Everything that needs
 * the tenant logo, colours or social links goes through {@link getBranding}
 * instead of calling the endpoint itself, so navigating around the app costs no
 * further requests.
 *
 * The cache is only re-read when it can actually be out of date:
 *
 *   - a branding save in this tab (the saved response replaces the cache)
 *   - a branding save in a sibling tab of the same browser (broadcast)
 *   - a branding save by another user, pushed over the notification hub — the
 *     shell forwards that to {@link refresh}
 *   - the user changing page or saving something ({@link refreshOnActivity})
 *   - the {@link REVALIDATE_AFTER_MS} fallback, while the tab is visible, which
 *     covers an idle user until the backend sends that push
 *
 * The response is only applied when it actually differs from what is on screen,
 * so a quiet re-read never repaints CSS variables or reloads the logo.
 */
@Injectable({ providedIn: 'root' })
export class CrmBrandingSyncService {
  private readonly companySettingsApiService = inject(CompanySettingsApiService);
  private readonly brandingStateService = inject(CrmBrandingStateService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private cached: MyTenantBrandingApiData | null = null;
  private inFlight$: Observable<MyTenantBrandingApiData | null> | null = null;
  private lastFetchedAt = 0;
  /** Bumped on teardown so a response that lands after logout is dropped. */
  private generation = 0;
  private applied: BrandingSnapshot | null = null;
  private channel: BroadcastChannel | null = null;
  private started = false;
  /** Set while the Settings page is previewing unsaved colours on the live DOM. */
  private suspended = false;

  private pollTimerId: ReturnType<typeof setInterval> | null = null;

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.startPolling();
      this.refresh();
    } else {
      this.stopPolling();
    }
  };

  private readonly onWindowFocus = (): void => this.refresh();

  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key === BRANDING_PING_KEY) {
      this.refresh(true);
    }
  };

  /** Called once by the shell. Reads branding now, then keeps the cache in sync. */
  start(): void {
    if (this.started) {
      return;
    }

    if (!this.isBrowser) {
      // Server render: a single read, so the SSR markup already carries the
      // tenant logo and palette.
      this.getBranding().subscribe();
      return;
    }

    this.started = true;

    this.openChannel();
    window.addEventListener('storage', this.onStorage);
    window.addEventListener('focus', this.onWindowFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.refresh(true);
    this.startPolling();
  }

  /** Called by the shell on teardown (logout / leaving the authenticated area). */
  stop(): void {
    if (!this.isBrowser) {
      return;
    }
    this.started = false;

    this.stopPolling();

    this.generation++;
    this.inFlight$ = null;
    this.cached = null;
    this.applied = null;
    this.lastFetchedAt = 0;

    window.removeEventListener('storage', this.onStorage);
    window.removeEventListener('focus', this.onWindowFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    this.channel?.close();
    this.channel = null;
  }

  /**
   * The tenant branding, from the cache when there is one. Concurrent callers
   * share a single request. Pass `force` only where a stale copy is not
   * acceptable (the Company Settings edit screen, right after a logo upload).
   *
   * Emits `null` when branding could not be read; it never errors.
   */
  getBranding(force = false): Observable<MyTenantBrandingApiData | null> {
    if (this.inFlight$) {
      return this.inFlight$;
    }
    if (!force && this.cached) {
      return of(this.cached);
    }

    const generation = this.generation;
    const request$ = this.companySettingsApiService.getMyTenantBranding().pipe(
      map((response) =>
        (response?.isSuccess ?? response?.success ?? true) ? (response?.data ?? null) : null,
      ),
      // A failed read must never disturb the theme already on screen, and must
      // never surface an error to a user who did nothing.
      catchError(() => of(null)),
      tap((data) => {
        if (generation !== this.generation) {
          return;
        }
        this.lastFetchedAt = Date.now();
        if (data) {
          this.cached = data;
          if (!this.suspended) {
            this.applyIfChanged(this.toSnapshot(data));
          }
        }
      }),
      finalize(() => {
        if (this.inFlight$ === request$) {
          this.inFlight$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlight$ = request$;
    return request$;
  }

  /**
   * Re-reads branding from the API. Skipped when the cached copy is younger than
   * {@link REVALIDATE_AFTER_MS} — pass `force` for the cases that must not be
   * throttled (start-up, a save elsewhere).
   */
  refresh(force = false): void {
    if (!this.isBrowser || this.suspended) {
      return;
    }
    if (!force && Date.now() - this.lastFetchedAt < REVALIDATE_AFTER_MS) {
      return;
    }

    this.getBranding(true).subscribe();
  }

  /**
   * Re-reads branding because the user just did something — a route change or a
   * save. Only throttled by {@link ACTIVITY_THROTTLE_MS}, so a change made by
   * another user shows up on the very next thing this user does.
   */
  refreshOnActivity(): void {
    if (!this.started || this.suspended) {
      return;
    }
    if (Date.now() - this.lastFetchedAt < ACTIVITY_THROTTLE_MS) {
      return;
    }

    this.getBranding(true).subscribe();
  }

  /**
   * Stops incoming branding from being applied. The Settings page writes unsaved
   * colours straight onto the document for a live preview; a re-read landing in
   * the middle of that would snap the preview back to the saved palette.
   */
  suspend(): void {
    this.suspended = true;
  }

  /** Re-enables syncing and immediately re-reads, discarding any unsaved preview. */
  resume(): void {
    if (!this.suspended) {
      return;
    }
    this.suspended = false;
    // The preview left the DOM in an unknown state, so force a full re-apply.
    this.applied = null;
    this.refresh(true);
  }

  /**
   * Announces a branding change made in this tab. Call it right after a successful
   * save, with the saved branding, so the cache holds it and sibling tabs pick the
   * new theme up straight away.
   */
  publishLocalChange(saved?: MyTenantBrandingApiData | null): void {
    if (!this.isBrowser) {
      return;
    }

    // A save turns the preview into the saved truth, so any suspension ends here.
    this.suspended = false;

    // What we just saved is the new cache and the applied state, so nothing in
    // this tab needs to read it back.
    if (saved) {
      this.cached = saved;
      this.applied = this.toSnapshot(saved);
      this.lastFetchedAt = Date.now();
    }

    const stamp = String(Date.now());
    this.channel?.postMessage(stamp);

    // `storage` fires in the *other* tabs only, which is exactly what we want.
    try {
      localStorage.setItem(BRANDING_PING_KEY, stamp);
    } catch {
      // Private-mode / blocked storage: those tabs catch up on their next re-read.
    }
  }

  private applyIfChanged(next: BrandingSnapshot): void {
    const current = this.applied;
    const colorsChanged =
      !current ||
      current.primaryColor !== next.primaryColor ||
      current.secondaryColor !== next.secondaryColor;
    const logoChanged = !current || current.logoUrl !== next.logoUrl;

    this.applied = next;

    if (colorsChanged) {
      this.brandingStateService.setColors(
        next.primaryColor || undefined,
        next.secondaryColor || undefined,
      );
    }

    if (logoChanged) {
      // A changed URL means a different file, so bust the cache on the way in —
      // the browser would otherwise keep serving the previous tenant logo.
      this.brandingStateService.setLogoUrl(
        current && next.logoUrl
          ? this.brandingStateService.withCacheBust(next.logoUrl)
          : next.logoUrl,
      );
    }
  }

  private toSnapshot(data: MyTenantBrandingApiData): BrandingSnapshot {
    return {
      primaryColor: this.normalizeColor(data.primaryColor),
      secondaryColor: this.normalizeColor(data.secondaryColor),
      logoUrl: data.logoUrl?.trim() ?? '',
    };
  }

  /** `#AB12CD`, `ab12cd` and `#ab12cd` all describe the same colour. */
  private normalizeColor(color: string | null | undefined): string {
    const clean = color?.trim().replace(/^#/, '').toLowerCase() ?? '';
    return clean ? `#${clean}` : '';
  }

  private openChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.channel = new BroadcastChannel(BRANDING_CHANNEL);
    this.channel.onmessage = () => this.refresh(true);
  }

  private startPolling(): void {
    if (this.pollTimerId !== null || !this.started || REVALIDATE_AFTER_MS <= 0) {
      return;
    }
    if (document.visibilityState !== 'visible') {
      return;
    }
    this.pollTimerId = setInterval(() => this.refresh(), REVALIDATE_AFTER_MS);
  }

  private stopPolling(): void {
    if (this.pollTimerId !== null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    }
  }
}
